import api from '@/shared/api';
import {
  buildTruthySearchParams,
  handleApiError,
  requestExcelBlobExport,
  type BulkDeleteResult,
  withQuery,
} from '@/shared/lib';
import type {
  FunctionalActivity,
  FunctionalActivityMember,
  FunctionalActivitySearchParams,
  PaginatedFunctionalActivityMembersResponse,
  PaginatedFunctionalActivityResponse,
  PaginationMetadata,
} from '../model/types';
import type { AxiosResponse } from 'axios';

const FUNCTIONAL_ACTIVITY_MEMBERS_PAGE_SIZE = 100;
const MAX_FUNCTIONAL_ACTIVITY_MEMBERS_PAGES = 1000;

export interface ExportFunctionalActivitiesExcelParams {
  search?: string;
  sort?: string;
  order?: string;
  active?: boolean;
  branchId?: number;
  departmentId?: number;
}

const FUNCTIONAL_ACTIVITIES_EXPORT_QUERY_KEYS = [
  'search',
  'sort',
  'order',
  'active',
  'branchId',
  'departmentId',
] as const;

class FunctionalActivityApi {
  async search(
    params: FunctionalActivitySearchParams
  ): Promise<{ data: FunctionalActivity[]; metadata: PaginationMetadata }> {
    try {
      const response: AxiosResponse<PaginatedFunctionalActivityResponse> = await api.get(
        withQuery('/functional-activities', params)
      );

      return {
        data: response.data.data ?? [],
        metadata: response.data.metadata ?? {
          total: 0,
          page: 1,
          itemsPerPage: 10,
          totalPages: 0,
        },
      };
    } catch (error) {
      console.error('Erro ao buscar atividades funcionais:', error);
      throw error;
    }
  }

  async getById(id: number): Promise<FunctionalActivity> {
    try {
      const response: AxiosResponse<{ success: boolean; data: FunctionalActivity }> = await api.get(
        `/functional-activities/${id}`
      );
      return response.data.data;
    } catch (error: unknown) {
      throw handleApiError(error, 'Erro ao buscar atividade funcional');
    }
  }

  async getPermissions(id: number): Promise<
    Array<{
      id: number;
      name: string;
      description?: string;
      permissionCategoryId?: number;
      permissionCategory?: {
        id: number;
        name: string;
      };
    }>
  > {
    try {
      const response: AxiosResponse<{
        success: boolean;
        data: Array<{
          id: number;
          name: string;
          description?: string;
          permissionCategoryId?: number;
          permissionCategory?: {
            id: number;
            name: string;
          };
        }>;
      }> =
        await api.get(`/functional-activities/${id}/permissions`, {
          params: { page: 1, itemsPerPage: 1000 },
        });
      return response.data.data;
    } catch (error: unknown) {
      throw handleApiError(error, 'Erro ao buscar permissões da atividade funcional');
    }
  }

  async delete(id: number): Promise<void> {
    try {
      await api.delete(`/functional-activities/${id}`);
    } catch (error: unknown) {
      throw handleApiError(error, 'Erro ao deletar atividade funcional');
    }
  }

  async getMembers(id: number): Promise<FunctionalActivityMember[]> {
    try {
      const members: FunctionalActivityMember[] = [];
      let page = 1;
      let hasNextPage = false;

      do {
        if (page > MAX_FUNCTIONAL_ACTIVITY_MEMBERS_PAGES) {
          throw new Error('Limite de páginas excedido ao buscar membros da atividade funcional');
        }

        const response: AxiosResponse<PaginatedFunctionalActivityMembersResponse> = await api.get(
          withQuery(`/functional-activities/${id}/members`, {
            page,
            itemsPerPage: FUNCTIONAL_ACTIVITY_MEMBERS_PAGE_SIZE,
          })
        );
        const pageMembers = response.data.data ?? [];
        const metadata = response.data.metadata;

        members.push(...pageMembers);

        hasNextPage = metadata.isNextPage;
        page = metadata.page + 1;
      } while (hasNextPage);

      return members;
    } catch (error) {
      console.error('Erro ao buscar membros da atividade funcional:', error);
      throw error;
    }
  }

  async bulkDelete(ids: number[]): Promise<{ data: BulkDeleteResult }> {
    try {
      const response = await api.delete('/functional-activities/bulk', {
        data: { ids },
      });
      return { data: response.data.data };
    } catch (error: unknown) {
      throw handleApiError(error, 'Erro ao deletar atividades funcionais em lote');
    }
  }

  async create(data: { name: string; branchId: number }): Promise<FunctionalActivity> {
    const response = await api.post('/functional-activities', data);
    return response.data.data;
  }

  async update(id: number, data: { name: string }): Promise<FunctionalActivity> {
    const response = await api.patch(`/functional-activities/${id}`, data);
    return response.data.data;
  }

  async clonePermissions(targetId: number, sourceId: number): Promise<void> {
    await api.post(`/functional-activities/${targetId}/clone-permissions`, { sourceId });
  }

  async exportFunctionalActivitiesExcel(
    params: ExportFunctionalActivitiesExcelParams
  ): Promise<{ blob: Blob; exportedCount: number; filename: string }> {
    const queryParams = buildTruthySearchParams(params, FUNCTIONAL_ACTIVITIES_EXPORT_QUERY_KEYS);

    return requestExcelBlobExport(
      () =>
        api.get(`/functional-activities/export/excel?${queryParams.toString()}`, {
          responseType: 'blob',
        }),
      'atividades-funcionais.xlsx',
      'Tente novamente.'
    );
  }
}

export const functionalActivityApi = new FunctionalActivityApi();
